import { useEffect, useState } from 'react'

type Lage = 'redo' | 'skickar' | 'rostat' | 'klart' | 'fel'

/** Tumme upp/ner-feedback i slutet av kapitel, lektioner och arbetsområden.
 *  Tummen loggas direkt vid klick (POST /api/feedback → Worker → D1); därefter
 *  visas en fritextruta som kompletterar samma rad. localStorage kommer ihåg
 *  att besökaren redan tyckt till om objektet, så rutan inte tigger igen. */
export default function FeedbackRuta({
  typ,
  objektId,
  benamning,
}: {
  typ: 'kapitel' | 'lektion' | 'arbetsomrade'
  objektId: string
  benamning: string
}) {
  const minnesnyckel = `feedback:${typ}:${objektId}`
  const [lage, setLage] = useState<Lage>('redo')
  const [omdome, setOmdome] = useState<'upp' | 'ner' | null>(null)
  const [feedbackId, setFeedbackId] = useState<string | null>(null)
  const [kommentar, setKommentar] = useState('')
  const [skickarKommentar, setSkickarKommentar] = useState(false)

  // Nollställ när objektet byts (sidorna återanvänds vid navigering mellan
  // t.ex. lektioner) och läs ur minnet om besökaren redan tyckt till.
  useEffect(() => {
    setOmdome(null)
    setFeedbackId(null)
    setKommentar('')
    setSkickarKommentar(false)
    setLage(window.localStorage.getItem(minnesnyckel) ? 'klart' : 'redo')
  }, [minnesnyckel])

  const rosta = async (val: 'upp' | 'ner') => {
    setOmdome(val)
    setLage('skickar')
    try {
      const svar = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typ, objekt_id: objektId, omdome: val }),
      })
      if (!svar.ok) throw new Error(String(svar.status))
      const { id } = (await svar.json()) as { id: string }
      setFeedbackId(id)
      window.localStorage.setItem(minnesnyckel, val)
      setLage('rostat')
    } catch {
      setLage('fel')
    }
  }

  const skickaKommentar = async () => {
    if (!feedbackId || !kommentar.trim()) return
    setSkickarKommentar(true)
    try {
      const svar = await fetch('/api/feedback/kommentar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: feedbackId, kommentar: kommentar.trim() }),
      })
      if (!svar.ok) throw new Error(String(svar.status))
      setLage('klart')
    } catch {
      setLage('fel')
    } finally {
      setSkickarKommentar(false)
    }
  }

  return (
    <section className="feedbackruta no-print" aria-label="Tyck till">
      {lage === 'klart' ? (
        <p className="feedbackruta-tack" role="status">Tack för din feedback! 💜</p>
      ) : lage === 'fel' ? (
        <p className="feedbackruta-tack" role="status">
          Det gick inte att skicka just nu — försök gärna igen senare.
        </p>
      ) : lage === 'rostat' ? (
        <>
          <p className="feedbackruta-tack" role="status">Tack!</p>
          <label className="feedbackruta-mer">
            Vill du berätta mer? Vad var bra — och vad kan bli bättre?
            <textarea
              rows={4}
              maxLength={5000}
              value={kommentar}
              onChange={(e) => setKommentar(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="knapp knapp-primar knapp-liten"
            onClick={skickaKommentar}
            disabled={skickarKommentar || !kommentar.trim()}
          >
            {skickarKommentar ? 'Skickar …' : 'Skicka'}
          </button>
        </>
      ) : (
        <>
          <p className="feedbackruta-fraga">Vad tyckte du om {benamning}?</p>
          <div className="feedbackruta-tummar">
            <button
              type="button"
              aria-label="Tumme upp"
              className={omdome === 'upp' ? 'vald' : ''}
              disabled={lage === 'skickar'}
              onClick={() => rosta('upp')}
            >
              👍
            </button>
            <button
              type="button"
              aria-label="Tumme ner"
              className={omdome === 'ner' ? 'vald' : ''}
              disabled={lage === 'skickar'}
              onClick={() => rosta('ner')}
            >
              👎
            </button>
          </div>
        </>
      )}
    </section>
  )
}
